import { levelPayment, roundTo } from "@/lib/tool-kit/finance/tvm";

/**
 * Repayment schedules for several loans, modelled by formula.
 *
 * These are *shapes*, not named programs: level payments over a chosen term, or payments
 * that start lower and step up by a fixed percentage every 24 months and still repay the
 * balance exactly over the chosen term. Interest accrues monthly at the annual rate ÷ 12
 * on the balance from the first payment; in-school interest, grace periods,
 * capitalization events and income-driven formulas are not modelled. Money is rounded to
 * the currency's minor unit each month, and a loan's last scheduled payment clears
 * whatever balance rounding leaves.
 */

export interface Loan {
  name: string;
  balance: number;
  /** Annual rate as a decimal. */
  rate: number;
}

export type PlanKind = "standard" | "extended" | "graduated";
export type Strategy = "none" | "avalanche" | "snowball";

export interface Plan {
  kind: PlanKind;
  /** Term in months. */
  months: number;
  /** Step-up every 24 months for the graduated shape, as a decimal. */
  step: number;
}

const STEP_INTERVAL = 24;
const MAX_MONTHS = 720;

/** The un-rounded payment multiplier for month m (1-based): 1 for level, stepped otherwise. */
function multiplier(plan: Plan, month: number): number {
  if (plan.kind !== "graduated") {
    return 1;
  }
  return (1 + plan.step) ** Math.floor((month - 1) / STEP_INTERVAL);
}

/**
 * The payment in each month 1…T for one loan: `payments[m - 1]`.
 * Level shapes use the annuity payment; the graduated shape solves for the first payment
 * P0 so that the stepped payments discount to the balance exactly.
 */
export function paymentSchedule(
  loan: Loan,
  plan: Plan,
  digits: number
): number[] {
  const i = loan.rate / 12;
  const months = plan.months;
  if (plan.kind !== "graduated") {
    const level = roundTo(levelPayment(loan.balance, i, months), digits);
    return Array.from({ length: months }, () => level);
  }
  let weight = 0;
  for (let m = 1; m <= months; m += 1) {
    weight += multiplier(plan, m) / (1 + i) ** m;
  }
  const first = loan.balance / weight;
  return Array.from({ length: months }, (_, k) =>
    roundTo(first * multiplier(plan, k + 1), digits)
  );
}

export interface LoanOutcome {
  /** Month in which the balance reached zero; null if it never did. */
  payoffMonth: number | null;
  interest: number;
}

export interface Simulation {
  months: number;
  totalInterest: number;
  totalPaid: number;
  /** Everything paid in month 1, and the largest monthly total. */
  firstPayment: number;
  peakPayment: number;
  perLoan: LoanOutcome[];
  /** Combined balance at the end of each year, starting with year 0. */
  yearlyBalance: number[];
  /** True if a balance was still open after the maximum number of months. */
  incomplete: boolean;
}

interface State {
  balances: number[];
  interest: number[];
  payoff: (number | null)[];
}

/** Open loans in the order extra money should reach them. */
function priority(
  loans: readonly Loan[],
  balances: readonly number[],
  strategy: Strategy
): number[] {
  const open = loans.map((_, index) => index).filter((x) => balances[x] > 0);
  if (strategy === "avalanche") {
    return open.sort(
      (a, b) => loans[b].rate - loans[a].rate || balances[a] - balances[b]
    );
  }
  return open.sort(
    (a, b) => balances[a] - balances[b] || loans[b].rate - loans[a].rate
  );
}

/**
 * Pay every loan month by month.
 *
 * The monthly budget is the sum of every loan's scheduled payment for that month —
 * paid-off loans keep contributing theirs, which is the "rollover" — plus `extra`. Each
 * open loan gets its scheduled payment first; whatever is left goes to the loans in
 * strategy order. With `strategy: "none"` nothing is redirected.
 */
export function simulate(input: {
  loans: readonly Loan[];
  plan: Plan;
  extra: number;
  strategy: Strategy;
  digits: number;
}): Simulation {
  const { loans, plan, extra, strategy, digits } = input;
  const schedules = loans.map((loan) => paymentSchedule(loan, plan, digits));
  const state: State = {
    balances: loans.map((loan) => loan.balance),
    interest: loans.map(() => 0),
    payoff: loans.map(() => null),
  };
  const yearlyBalance = [roundTo(sum(state.balances), digits)];
  let totalPaid = 0;
  let firstPayment = 0;
  let peakPayment = 0;
  let month = 0;
  while (state.balances.some((b) => b > 0) && month < MAX_MONTHS) {
    month += 1;
    const paid = payMonth(loans, schedules, state, {
      month,
      months: plan.months,
      extra: strategy === "none" ? 0 : extra,
      strategy,
      digits,
    });
    totalPaid += paid;
    firstPayment = month === 1 ? paid : firstPayment;
    peakPayment = Math.max(peakPayment, paid);
    if (month % 12 === 0) {
      yearlyBalance.push(roundTo(sum(state.balances), digits));
    }
  }
  if (month % 12 !== 0) {
    yearlyBalance.push(roundTo(sum(state.balances), digits));
  }
  return {
    months: month,
    totalInterest: roundTo(sum(state.interest), digits),
    totalPaid: roundTo(totalPaid, digits),
    firstPayment,
    peakPayment,
    perLoan: loans.map((_, x) => ({
      payoffMonth: state.payoff[x],
      interest: roundTo(state.interest[x], digits),
    })),
    yearlyBalance,
    incomplete: state.balances.some((b) => b > 0),
  };
}

function sum(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total;
}

function payMonth(
  loans: readonly Loan[],
  schedules: readonly number[][],
  state: State,
  ctx: {
    month: number;
    months: number;
    extra: number;
    strategy: Strategy;
    digits: number;
  }
): number {
  const { month, months, digits } = ctx;
  let budget = ctx.extra;
  let paid = 0;
  for (const [x, loan] of loans.entries()) {
    budget += schedules[x][month - 1] ?? 0;
    if (state.balances[x] > 0) {
      const interest = roundTo((state.balances[x] * loan.rate) / 12, digits);
      state.balances[x] = roundTo(state.balances[x] + interest, digits);
      state.interest[x] += interest;
    }
  }
  const pay = (x: number, amount: number) => {
    const actual = Math.min(amount, state.balances[x]);
    state.balances[x] = roundTo(state.balances[x] - actual, digits);
    paid += actual;
    budget -= actual;
    if (state.balances[x] <= 0 && state.payoff[x] === null) {
      state.payoff[x] = month;
    }
  };
  for (let x = 0; x < loans.length; x += 1) {
    if (state.balances[x] > 0) {
      // At the end of the schedule a loan pays whatever rounding left.
      pay(
        x,
        month >= months ? state.balances[x] : (schedules[x][month - 1] ?? 0)
      );
    }
  }
  if (ctx.strategy !== "none") {
    for (const x of priority(loans, state.balances, ctx.strategy)) {
      pay(x, Math.max(0, budget));
    }
  }
  return paid;
}
